import React from 'react';
import Icon from '../../../components/AppIcon';

const PricingTransparency = () => {
  const pricingPlans = [
    {
      type: 'Sofortberatung',
      price: '2,99',
      unit: 'EUR/Minute',
      description: 'Direkter Anruf ohne Wartezeit',
      features: [
        'Sofortige Verbindung',
        'Alle Rechtsgebiete',
        'Erfahrene Anwälte',
        '24/7 verfügbar',
        'Keine Mindestgesprächsdauer',
        'Sekundengenau abgerechnet'
      ],
      highlight: false,
      icon: 'Phone'
    },
    {
      type: 'Terminberatung',
      price: '89,00',
      unit: 'EUR/30 Min',
      description: 'Geplante ausführliche Beratung',
      features: [
        'Fester Terminslot',
        'Spezialisierte Anwälte',
        'Ausführliche Beratung',
        'Vorbereitung möglich',
        'Schriftliche Zusammenfassung',
        'Follow-up Gespräch inklusive'
      ],
      highlight: true,
      icon: 'Calendar'
    },
    {
      type: 'Schriftliche Beratung',
      price: '89,00',
      unit: 'EUR',
      description: 'Schriftliche rechtliche Beratung',
      features: [
        'Professionelle Beratung',
        'Binnen 24h Antwort',
        'Schriftliche Ausarbeitung',
        'Handlungsempfehlungen',
        'Rechtliche Einschätzung',
        'Nachfragemöglichkeit'
      ],
      highlight: false,
      icon: 'FileText'
    }
  ];

  const additionalInfo = [
    {
      icon: 'Shield',
      title: 'Keine versteckten Kosten',
      description: 'Alle Preise sind transparent und final. Keine Zusatzgebühren oder versteckte Kosten.'
    },
    {
      icon: 'Clock',
      title: 'Sekundengenaue Abrechnung',
      description: 'Bei der Sofortberatung zahlen Sie nur für die tatsächlich genutzte Zeit.'
    },
    {
      icon: 'CreditCard',
      title: 'Flexible Zahlungsmethoden',
      description: 'Bezahlung per Kreditkarte, PayPal, SEPA-Lastschrift oder Rechnung möglich.'
    },
    {
      icon: 'Receipt',
      title: 'Steuerlich absetzbar',
      description: 'Alle Beratungskosten können als Werbungskosten steuerlich geltend gemacht werden.'
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Transparente Preise ohne Überraschungen
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Faire und transparente Preisgestaltung für professionelle Rechtsberatung. 
            Wählen Sie das Beratungsformat, das am besten zu Ihrem Bedarf passt.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {pricingPlans?.map((plan, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-2xl shadow-elevated border-2 transition-all duration-300 hover:shadow-lg hover:-translate-y-2 ${
                plan?.highlight 
                  ? 'border-primary ring-2 ring-primary/20' :'border-slate-200'
              }`}
            >
              {plan?.highlight && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-primary text-white px-4 py-1 rounded-full text-sm font-medium">
                    Beliebteste Option
                  </div>
                </div>
              )}

              <div className="p-8">
                <div className="text-center mb-8">
                  <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${
                    plan?.highlight ? 'bg-primary' : 'bg-surface'
                  }`}>
                    <Icon 
                      name={plan?.icon} 
                      size={24} 
                      color={plan?.highlight ? 'white' : 'var(--color-primary)'} 
                    />
                  </div>
                  
                  <h3 className="text-2xl font-bold text-text-primary mb-2">
                    {plan?.type}
                  </h3>
                  
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-primary">
                      {plan?.price}
                    </span>
                    {plan?.unit && (
                      <span className="text-text-secondary ml-1">
                        {plan?.unit}
                      </span>
                    )}
                  </div>
                  
                  <p className="text-text-secondary">
                    {plan?.description}
                  </p>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan?.features?.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start space-x-3">
                      <Icon 
                        name="Check" 
                        size={16} 
                        color="var(--color-trust-builder)" 
                        className="mt-0.5 flex-shrink-0"
                      />
                      <span className="text-sm text-text-secondary">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {additionalInfo?.map((info, index) => (
            <div key={index} className="text-center">
              <div className="w-12 h-12 bg-surface rounded-lg flex items-center justify-center mx-auto mb-4">
                <Icon name={info?.icon} size={24} color="var(--color-primary)" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                {info?.title}
              </h3>
              <p className="text-sm text-text-secondary">
                {info?.description}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-16 bg-surface rounded-2xl p-8">
          <div className="text-center">
            <Icon name="Info" size={32} color="var(--color-primary)" className="mx-auto mb-4" />
            <h3 className="text-xl font-bold text-text-primary mb-4">
              Wichtige Preisinformationen
            </h3>
            <div className="max-w-4xl mx-auto text-left space-y-4 text-text-secondary">
              <p>
                <strong>Sofortberatung:</strong> Die Abrechnung erfolgt sekundengenau ab dem Moment der Verbindung mit dem Anwalt. 
                Wartezeiten in der Telefonschleife werden nicht berechnet.
              </p>
              <p>
                <strong>Terminberatung:</strong> Der Preis gilt für 30 Minuten intensive Beratung. 
                Verlängerungen sind in 15-Minuten-Schritten zu 44,50 EUR möglich.
              </p>
              <p>
                <strong>Schriftliche Beratung:</strong> Professionelle rechtliche Beratung in schriftlicher Form. 
                Sie erhalten eine ausführliche Antwort binnen 24 Stunden mit rechtlicher Einschätzung und Handlungsempfehlungen.
              </p>
              <p>
                <strong>Alle Preise:</strong> Verstehen sich inklusive der gesetzlichen Mehrwertsteuer. 
                Zusätzliche Gebühren fallen nicht an.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingTransparency;